import type {
  SmartCreationStrategyDiagnostic,
  SmartCreationStrategyOperation,
  SmartCreationStrategyRecommendedTarget,
  SmartCreationStrategyTarget,
  SmartCreationStrategyTargetIntent,
  SmartCreationStrategyTargetSource,
  SmartCreationTargetingStrategyBranch,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import {
  effectiveSmartCreationStrategyLanes,
  smartCreationStrategyTargetLane,
} from "@adpilot/shared";

export interface EditableTargetValue {
  intent: SmartCreationStrategyTargetIntent;
  bid: number;
  included: boolean;
}

export interface LocalTarget extends EditableTargetValue {
  id: string;
  local: true;
  candidate?: SmartCreationStrategyRecommendedTarget;
}

export interface TargetRow extends EditableTargetValue {
  id: string;
  local: boolean;
  persisted?: SmartCreationStrategyTarget;
  candidate?: SmartCreationStrategyRecommendedTarget;
}

export type TargetTypeFilter = "all" | SmartCreationStrategyTargetIntent["type"];
export type TargetSourceFilter = "all" | "entered" | "legacy" | SmartCreationStrategyTargetSource;
export type TargetStatusFilter = "all" | "selected" | "excluded" | "issue";
export type EntryType = "keyword" | "product" | "category";
export type RestoreSource = "entered" | "recommended";
export type EntryMatchType = "exact" | "phrase" | "broad";

export function strategyLabelKey(kind: SmartCreationTargetingStrategyKind): string {
  return `strategy.${kind}`;
}

export function targetLabel(intent: SmartCreationStrategyTargetIntent): string {
  if (intent.type === "auto") return intent.expressionType.replaceAll("_", " ");
  if (intent.type === "keyword") return intent.keyword;
  if (intent.type === "product") return intent.asin;
  if (intent.type === "category_brand") return `${intent.categoryId} / ${intent.brandId}`;
  return intent.categoryId;
}

export function targetFingerprint(intent: SmartCreationStrategyTargetIntent): string {
  if (intent.type === "auto") return `auto:${intent.expressionType}`;
  if (intent.type === "keyword") {
    return `keyword:${intent.keyword.trim().toLocaleLowerCase()}:${intent.matchType}`;
  }
  if (intent.type === "product")
    return `product:${intent.asin.toUpperCase()}:${intent.matchType ?? "exact"}`;
  if (intent.type === "category_brand")
    return `category_brand:${intent.categoryId}:${intent.brandId}`;
  return `category:${intent.categoryId}`;
}

export function targetSource(target: TargetRow): TargetSourceFilter {
  if (target.local) return "entered";
  if (target.candidate) return target.candidate.source;
  if (target.persisted?.provenance.mode !== "entered") {
    return target.persisted?.provenance.originalRecommendation.source ?? "entered";
  }
  if (target.persisted?.provenance.origin === "legacy") return "legacy";
  return target.persisted?.provenance.origin === "system_required" ? "system_required" : "entered";
}

export function diagnosticTargetId(target: TargetRow): string {
  return target.candidate?.id ?? target.id;
}

export function targetValue(target: SmartCreationStrategyTarget): EditableTargetValue {
  return { intent: target.intent, bid: target.bid, included: target.included };
}

export function allowedMatchTypes(kind: SmartCreationTargetingStrategyKind) {
  if (kind === "performance") return ["exact"] as const;
  if (kind === "research") return ["broad", "phrase"] as const;
  return ["exact", "phrase", "broad"] as const;
}

export function initialMatchType(branch: SmartCreationTargetingStrategyBranch) {
  return (
    allowedMatchTypes(branch.kind).find(
      (matchType) =>
        !branch.enabledLanes ||
        effectiveSmartCreationStrategyLanes(branch).includes(
          smartCreationStrategyTargetLane(branch.kind, { type: "keyword", keyword: "", matchType }),
        ),
    ) ?? allowedMatchTypes(branch.kind)[0]
  );
}

export function sourceLabelKey(target: TargetRow): string {
  const source = targetSource(target);
  if (source === "entered") return "strategy.editor.userEntered";
  if (source === "legacy") return "strategy.editor.legacyDefault";
  return `strategy.editor.sources.${source}`;
}

export function statusFor(
  target: TargetRow,
  diagnostics: readonly SmartCreationStrategyDiagnostic[],
  locallyDuplicated: boolean,
): { key: string; variant: "neutral" | "success" | "warning" | "destructive" } {
  const targetDiagnostic = diagnostics.find(
    (item) => item.targetId === diagnosticTargetId(target) && item.blocking,
  );
  if (targetDiagnostic?.code === "duplicate_target" || locallyDuplicated) {
    return { key: "duplicateStatus", variant: "destructive" };
  }
  if (targetDiagnostic) return { key: "unsupportedStatus", variant: "destructive" };
  if (target.candidate && !target.local) return { key: "recommendedStatus", variant: "success" };
  if (!target.included) return { key: "excludedStatus", variant: "neutral" };
  return { key: "selectedStatus", variant: "neutral" };
}

export function respectsDecimalPrecision(value: number, decimals: number): boolean {
  const scale = 10 ** decimals;
  return Math.abs(value * scale - Math.round(value * scale)) < 1e-8;
}

export function canAcceptRecommendation(
  operations: readonly SmartCreationStrategyOperation[],
): boolean {
  return operations.length === 0;
}

export function canRestoreSource(operations: readonly SmartCreationStrategyOperation[]): boolean {
  return operations.length === 0;
}

export function acceptedTargetSelection(
  branch: SmartCreationTargetingStrategyBranch,
  selectedTargetId: string | null,
): string | null {
  if (!selectedTargetId?.startsWith("candidate:")) return selectedTargetId;
  const recommendationTargetId = selectedTargetId.slice("candidate:".length);
  return (
    branch.targets.find(
      (target) =>
        target.provenance.mode !== "entered" &&
        target.provenance.recommendationTargetId === recommendationTargetId,
    )?.id ?? selectedTargetId
  );
}

export function buildOperations(
  kind: SmartCreationTargetingStrategyKind,
  branch: SmartCreationTargetingStrategyBranch,
  rows: readonly TargetRow[],
  restoreSource: RestoreSource | null,
): SmartCreationStrategyOperation[] {
  if (kind === "automatic")
    return branch.targets.flatMap((original) => {
      const row = rows.find((candidate) => candidate.id === original.id);
      return row && row.included !== original.included
        ? [
            {
              operation: "set_target_included" as const,
              strategyKind: kind,
              targetId: original.id,
              included: row.included,
            },
          ]
        : [];
    });
  if (restoreSource)
    return [{ operation: "restore_source", strategyKind: kind, source: restoreSource }];
  const operations: SmartCreationStrategyOperation[] = [];
  const rowById = new Map(rows.map((row) => [row.id, row]));

  for (const original of branch.targets) {
    const row = rowById.get(original.id);
    if (!row) continue;
    if (row.included !== original.included) {
      operations.push({
        operation: "set_target_included",
        strategyKind: kind,
        targetId: original.id,
        included: row.included,
      });
    }
    const patch: { intent?: SmartCreationStrategyTargetIntent; bid?: number } = {};
    if (targetFingerprint(row.intent) !== targetFingerprint(original.intent))
      patch.intent = row.intent;
    if (row.bid !== original.bid) patch.bid = row.bid;
    if (patch.intent || patch.bid !== undefined) {
      operations.push({
        operation: "update_target",
        strategyKind: kind,
        targetId: original.id,
        patch,
      });
    }
  }

  for (const row of rows) {
    if (!row.local || !row.included) continue;
    operations.push({
      operation: "add_target",
      strategyKind: kind,
      target: { intent: row.intent, bid: row.bid },
    });
  }
  return operations;
}
